// AdminBotMatrix — Phase 11D Bot Performance Matrix.
//
// Per-category P&L view with per-(category, game) drill-down.
// Each category card has a kill-switch toggle; a master toggle at top
// flips all three at once for emergency disable.
//
// "Platform profit" is the sign-flipped sum of bot_net_p_l. A POSITIVE
// platform_profit_window number means the platform is making money on
// bots; a NEGATIVE number means bots are draining funds (admin should
// consider disabling that category).
//
// Phase 2 UI overhaul (2026-05-15): visual surfaces realigned with the
// player/agent arena-tinted glassmorphic look — hard borders → soft
// arena-tinted insetting rings, neutral palette → arena/neon tokens,
// fuchsia pills → violet (admin identity). Per-category gradient cards
// keep their semantic colour ramps (emerald / amber / rose) but switch
// to ring-1 ring-inset surrounds. Data + API calls unchanged.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
// Phase 19 / Batch 2: typed-confirm modal for DISABLE actions (N5/N6).
// Re-enable stays on the existing inline ConfirmModal — re-enable is
// safe and doesn't need to defeat sleepy misclicks.
import DangerConfirmModal from '@baaligames/shared-ui/components/DangerConfirmModal';

type WindowKey = 'all' | '24h' | '7d' | '30d';

interface PerGame {
  game: string;
  bet_outflow: number; prizes_in: number; refunds_in: number;
  bot_net_p_l: number; match_count: number; win_count: number;
  // 2026-05-17 (Alternative Fee Policy follow-up): per-game breakdown.
  platform_match_profit?: number;
  platform_fee_profit?:   number;
  platform_total_profit?: number;
}
interface Category {
  category: 'easy' | 'medium' | 'hard';
  bot_count: number; active_count: number; pool_balance: number;
  kill_switch_enabled: boolean;
  bet_outflow: number; prizes_in: number; refunds_in: number;
  bot_net_p_l: number; match_count: number; win_count: number; win_rate_pct: number;
  // 2026-05-17: platform-side breakdown surfaced by backend.
  platform_match_profit?: number;
  platform_fee_profit?:   number;
  platform_total_profit?: number;
  platform_fee_entries?:  number;
  per_game: PerGame[];
}
interface Matrix {
  window: WindowKey;
  master_enabled: boolean;
  all_disabled: boolean;
  platform_profit_window: number;
  // 2026-05-17: total now decomposes into these two sub-totals.
  platform_match_profit_window?: number;
  platform_fee_profit_window?:   number;
  total_bots: number; total_active: number;
  categories: Category[];
}

const WINDOWS: Array<{ k: WindowKey; label: string }> = [
  { k: '24h', label: '24h' }, { k: '7d', label: '7d' }, { k: '30d', label: '30d' }, { k: 'all', label: 'All' },
];

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;
const fmtSigned = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}₹${Math.abs(n).toLocaleString('en-IN')}`;

// Per-category card tone — soft gradient + inset ring per difficulty.
// Switched from `border` to `ring-1 ring-inset` so the cards visually
// sit on the new glassmorphic surface.
const CAT_TONE: Record<Category['category'], {
  grad: string; ring: string; iconBg: string; label: string; emoji: string;
}> = {
  easy: {
    grad: 'from-emerald-50 to-teal-50 dark:from-emerald-500/[0.08] dark:to-teal-500/[0.08]',
    ring: 'ring-emerald-200/60 dark:ring-emerald-400/30',
    iconBg: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30',
    label: 'text-emerald-700 dark:text-emerald-300',
    emoji: 'sentiment_satisfied',
  },
  medium: {
    grad: 'from-amber-50 to-orange-50 dark:from-amber-500/[0.08] dark:to-orange-500/[0.08]',
    ring: 'ring-amber-200/60 dark:ring-amber-400/30',
    iconBg: 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30',
    label: 'text-amber-700 dark:text-amber-300',
    emoji: 'cognition',
  },
  hard: {
    grad: 'from-rose-50 to-red-50 dark:from-rose-500/[0.08] dark:to-red-500/[0.08]',
    ring: 'ring-rose-200/60 dark:ring-rose-400/30',
    iconBg: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30',
    label: 'text-rose-700 dark:text-rose-300',
    emoji: 'local_fire_department',
  },
};

export default function BotMatrixPanel() {
  const [windowK, setWindowK] = useState<WindowKey>('7d');
  const [data, setData] = useState<Matrix | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState<{ kind: 'cat' | 'master'; payload: any } | null>(null);
  const [acting, setActing] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminApi.get<Matrix>(`/admin/bot-matrix?window=${windowK}`);
      setData(r);
    } catch (e) { setErr((e as ApiError).message || 'Failed to load matrix'); }
    finally { setLoading(false); }
  }, [windowK]);

  useEffect(() => { reload(); }, [reload]);

  const toggleExpanded = (cat: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const requestCategoryToggle = (category: Category['category'], enabled: boolean) => {
    setConfirming({ kind: 'cat', payload: { category, enabled } });
  };
  const requestMasterToggle = (enabled: boolean) => {
    setConfirming({ kind: 'master', payload: { enabled } });
  };
  const applyConfirm = async () => {
    if (!confirming) return;
    setActing(true);
    try {
      if (confirming.kind === 'cat') {
        await adminApi.post('/admin/bot-matrix/category-toggle', confirming.payload);
        setToast(`'${confirming.payload.category}' bots ${confirming.payload.enabled ? 'enabled' : 'disabled'}.`);
      } else {
        await adminApi.post('/admin/bot-matrix/master-toggle', confirming.payload);
        setToast(`All bots ${confirming.payload.enabled ? 'enabled' : 'disabled'}.`);
      }
      setConfirming(null);
      reload();
    } catch (e) {
      setErr((e as ApiError).message);
      setConfirming(null);
    } finally {
      setActing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        {err && <Banner tone="error">{err}</Banner>}
        {toast && <Banner tone="success">{toast}</Banner>}

        {/* Summary + master controls */}
        {data && (
          <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-5
                       bg-white dark:bg-arena-700/40
                       ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                       backdrop-blur
                       flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          >
            <div className="flex items-start gap-3 min-w-0">
              <span className="grid place-items-center w-10 h-10 rounded-xl shrink-0
                               bg-violet-100 text-violet-700 ring-1 ring-violet-200
                               dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30">
                <Icon name="trending_up" size={22} filled />
              </span>
              <div className="min-w-0">
                <div className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">
                  Platform profit on bots ({data.window})
                </div>
                <div className={`text-3xl font-display font-black tabular-nums mt-0.5 ${data.platform_profit_window >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                  {fmtSigned(data.platform_profit_window)}
                </div>
                {/* 2026-05-17: Match + Fees breakdown when backend
                    supplies the new fields (older deploys still render
                    just the headline). */}
                {(data.platform_match_profit_window !== undefined
                  && data.platform_fee_profit_window !== undefined) ? (
                  <div className="mt-1.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[11px] tabular-nums">
                    <span className="text-arena-600 dark:text-neon-200/65">
                      Match P&L: <span className={`font-bold ${data.platform_match_profit_window >= 0 ? 'text-emerald-600 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'}`}>{fmtSigned(data.platform_match_profit_window)}</span>
                    </span>
                    <span className="text-arena-400 dark:text-neon-200/40">+</span>
                    <span className="text-arena-600 dark:text-neon-200/65">
                      Platform fees: <span className={`font-bold ${data.platform_fee_profit_window >= 0 ? 'text-emerald-600 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'}`}>{fmtSigned(data.platform_fee_profit_window)}</span>
                    </span>
                  </div>
                ) : null}
                <div className="text-[12px] text-arena-600 dark:text-neon-200/60 mt-0.5">
                  {data.total_active} of {data.total_bots} bots active across {data.categories.length} categories
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {data.all_disabled && (
                <span className="px-2 py-1 rounded-full bg-rose-100 dark:bg-rose-400/15 text-rose-700 dark:text-rose-300 text-[10px] font-bold uppercase tracking-wider">
                  ALL DISABLED
                </span>
              )}
              {!data.master_enabled && !data.all_disabled && (
                <span className="px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-400/15 text-amber-700 dark:text-amber-300 text-[10px] font-bold uppercase tracking-wider">
                  PARTIAL
                </span>
              )}
              <button onClick={() => requestMasterToggle(!data.master_enabled)} disabled={loading}
                      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold text-white transition active:scale-95 disabled:opacity-50 ${
                        data.master_enabled
                          ? 'bg-rose-600 hover:bg-rose-500 active:bg-rose-700 shadow-[0_2px_10px_rgba(244,63,94,0.4)]'
                          : 'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 shadow-[0_2px_10px_rgba(16,185,129,0.4)]'
                      }`}>
                <Icon name={data.master_enabled ? 'block' : 'play_arrow'} size={13} />
                {data.master_enabled ? 'Disable ALL' : 'Enable ALL'}
              </button>
            </div>
          </motion.div>
        )}

        {/* Window selector — sticky under TopPillNav */}
        <motion.div
          initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          className="sticky top-0 z-10 -mx-2 px-2 sm:-mx-3 sm:px-3 lg:-mx-4 lg:px-4 py-2
                     backdrop-blur-md
                     bg-white/70 dark:bg-arena-900/60
                     flex items-center gap-2 flex-wrap"
        >
          <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">Window</span>
          {WINDOWS.map(w => (
            <button key={w.k} onClick={() => setWindowK(w.k)}
                    className={`shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold transition ring-1 ring-inset ${
                      windowK === w.k
                        ? 'bg-violet-600 text-white ring-violet-600 shadow-[0_2px_10px_rgba(139,92,246,0.4)]'
                        : 'bg-white text-arena-700 ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/80 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'
                    }`}>
              {w.label}
            </button>
          ))}
          <div className="flex-1" />
          <button onClick={reload}
                  className="grid place-items-center w-9 h-9 rounded-xl
                             bg-white dark:bg-arena-700/40
                             ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                             text-arena-700 dark:text-neon-200/80
                             hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
            <Icon name="refresh" size={16} />
          </button>
        </motion.div>

        {loading && !data && (
          <div className="text-center py-12 text-sm text-arena-600 dark:text-neon-200/60">
            <Icon name="progress_activity" size={20} className="animate-spin inline-block mr-2 align-middle" /> Loading…
          </div>
        )}

        {/* Category cards */}
        {data && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {data.categories.map((c, i) => {
              const tone = CAT_TONE[c.category];
              return (
                <motion.div key={c.category}
                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.04 * i }}
                            className={`rounded-2xl p-4
                                        ring-1 ring-inset ${tone.ring}
                                        bg-gradient-to-br ${tone.grad}
                                        shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                                        backdrop-blur ${
                              !c.kill_switch_enabled ? 'opacity-60 saturate-50' : ''
                            }`}>
                  <div className="flex items-start gap-3 mb-3">
                    <span className={`grid place-items-center w-10 h-10 rounded-xl shrink-0 ring-1 ${tone.iconBg}`}>
                      <Icon name={tone.emoji} size={20} filled />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className={`font-bold uppercase tracking-wider text-[13px] ${tone.label}`}>{c.category}</div>
                      <div className="text-[10.5px] text-arena-600 dark:text-neon-200/60">
                        {c.active_count} of {c.bot_count} active
                      </div>
                    </div>
                    {!c.kill_switch_enabled && (
                      <span className="text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold
                                       bg-rose-100 text-rose-700
                                       dark:bg-rose-400/15 dark:text-rose-300">DISABLED</span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[11.5px] mb-3">
                    <Stat label="Pool" value={inr(c.pool_balance)} />
                    <Stat label="Matches" value={c.match_count.toLocaleString('en-IN')} />
                    <Stat label="Wins" value={c.win_count.toLocaleString('en-IN')} />
                    <Stat label="Win rate" value={`${c.win_rate_pct.toFixed(1)}%`} />
                  </div>

                  {/* 2026-05-16 color/sign audit fix:
                      Previously this card displayed `bot_net_p_l` (the
                      BOTS' perspective sign) but coloured it from the
                      PLATFORM'S perspective — resulting in `+₹65` shown
                      in rose ("looks like a profit but it's a loss") and
                      `−₹176` shown in emerald ("looks like a loss but
                      it's a profit"). Total sign/color contradiction.
                      Fix: show platform's perspective (`-bot_net_p_l`)
                      so the SIGN and the COLOR both speak the same
                      language. `platformProfitForCategory > 0` = good. */}
                  {(() => {
                    // 2026-05-17 (Alternative Fee Policy follow-up):
                    // The headline platform_total_profit field is
                    // supplied by the backend and DECOMPOSES into
                    // platform_match_profit + platform_fee_profit. We
                    // render the total prominently and the breakdown
                    // beneath. Older deploys without the new fields
                    // fall back to the legacy match-only number.
                    const matchProfit = c.platform_match_profit ?? -c.bot_net_p_l;
                    const feeProfit   = c.platform_fee_profit   ?? 0;
                    const totalProfit = c.platform_total_profit ?? (matchProfit + feeProfit);

                    const profitColor =
                      totalProfit > 0 ? 'text-emerald-600 dark:text-emerald-400'
                      : totalProfit < 0 ? 'text-rose-600 dark:text-rose-400'
                      : 'text-arena-700 dark:text-neon-200/80';
                    const hint =
                      totalProfit > 0 ? 'Platform profiting'
                      : totalProfit < 0 ? 'Bots winning — platform paying'
                      : 'Break-even';

                    return (
                      <div className="rounded-xl p-3 mb-3
                                      bg-white/70 dark:bg-arena-900/40
                                      ring-1 ring-inset ring-white/50 dark:ring-arena-600/30">
                        <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">
                          Platform P&L · this category
                        </div>
                        <div className={`text-2xl font-display font-black tabular-nums ${profitColor}`}>
                          {fmtSigned(totalProfit)}
                        </div>
                        <div className="text-[10.5px] text-arena-600 dark:text-neon-200/60 mt-0.5">
                          {hint}
                        </div>
                        {/* Match + Fee breakdown — always shown when the
                            backend supplies the new fields (always under
                            the Alternative Fee Policy, post-restart). */}
                        {(c.platform_match_profit !== undefined && c.platform_fee_profit !== undefined) && (
                          <div className="mt-2 pt-2 border-t border-arena-200/40 dark:border-arena-600/30 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[10.5px] tabular-nums">
                            <span className="text-arena-600 dark:text-neon-200/65">
                              Match: <span className={`font-bold ${matchProfit >= 0 ? 'text-emerald-600 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'}`}>{fmtSigned(matchProfit)}</span>
                            </span>
                            <span className="text-arena-400 dark:text-neon-200/40">+</span>
                            <span className="text-arena-600 dark:text-neon-200/65">
                              Fees: <span className={`font-bold ${feeProfit >= 0 ? 'text-emerald-600 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'}`}>{fmtSigned(feeProfit)}</span>
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <button onClick={() => requestCategoryToggle(c.category, !c.kill_switch_enabled)}
                          className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[11.5px] font-bold text-white transition active:scale-95 ${
                            c.kill_switch_enabled
                              ? 'bg-rose-600 hover:bg-rose-500 active:bg-rose-700 shadow-[0_2px_10px_rgba(244,63,94,0.4)]'
                              : 'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 shadow-[0_2px_10px_rgba(16,185,129,0.4)]'
                          }`}>
                    <Icon name={c.kill_switch_enabled ? 'block' : 'play_arrow'} size={13} />
                    {c.kill_switch_enabled ? 'Disable category' : 'Enable category'}
                  </button>

                  {c.per_game.length > 0 && (
                    <button onClick={() => toggleExpanded(c.category)}
                            className="mt-3 w-full text-left text-[11px] font-semibold text-arena-700 dark:text-neon-200/75 hover:text-arena-900 dark:hover:text-white inline-flex items-center gap-1 transition">
                      <Icon name={expanded.has(c.category) ? 'expand_less' : 'expand_more'} size={14} />
                      Per-game breakdown ({c.per_game.length} games)
                    </button>
                  )}
                  <AnimatePresence>
                    {expanded.has(c.category) && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                                  className="overflow-hidden">
                        <div className="mt-2 space-y-1">
                          {c.per_game.map(g => {
                            // Same sign-flip fix as the category card —
                            // show platform's perspective so sign and
                            // color align (positive green / negative rose).
                            const platformProfitForGame = -g.bot_net_p_l;
                            return (
                              <div key={g.game} className="flex items-center text-[11.5px] py-1.5 border-t border-white/50 dark:border-arena-600/30">
                                <span className="capitalize w-16 font-medium text-arena-800 dark:text-neon-200/90">{g.game}</span>
                                <span className="text-arena-600 dark:text-neon-200/60 w-16 text-right tabular-nums">{g.match_count}m</span>
                                <span className={`flex-1 text-right font-bold tabular-nums ${
                                  platformProfitForGame > 0 ? 'text-emerald-600 dark:text-emerald-400'
                                  : platformProfitForGame < 0 ? 'text-rose-600 dark:text-rose-400'
                                  : 'text-arena-600 dark:text-neon-200/60'}`}>
                                  {fmtSigned(platformProfitForGame)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Footer link to per-bot list */}
        <div className="text-center pt-1">
          <Link to="/settings/bots"
                className="inline-flex items-center gap-1 text-[12px] font-semibold
                           text-violet-600 hover:text-violet-700
                           dark:text-violet-300 dark:hover:text-violet-200 transition">
            Open per-bot list <Icon name="open_in_new" size={12} />
          </Link>
        </div>
      </div>

      {/* Batch 2 N5/N6: DISABLE actions go through the typed-confirm
          DangerConfirmModal (admin must literally type "DISABLE" before
          the button enables). Re-enable stays on the existing simple
          ConfirmModal. */}
      {confirming && confirming.payload.enabled === false && (
        <DangerConfirmModal
          open={true}
          onClose={() => setConfirming(null)}
          onConfirm={applyConfirm}
          title={confirming.kind === 'master'
            ? 'Disable ALL bot categories?'
            : `Disable '${confirming.payload.category}' bots?`}
          description={confirming.kind === 'master'
            ? 'NO bots will be picked for new matchmaking queues across any category. Existing in-flight matches with bots already in them will finish normally. Players who would have been matched against a bot will wait until a human joins.'
            : `'${confirming.payload.category}' bots will not be picked for new matches. Existing in-flight matches will finish. Effective within ~5s.`}
          confirmLabel="Disable"
          cancelLabel="Keep enabled"
          confirmWord="DISABLE"
        />
      )}
      {confirming && confirming.payload.enabled === true && (
        <ConfirmModal confirming={confirming} busy={acting}
                      onCancel={() => setConfirming(null)} onConfirm={applyConfirm} />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg px-2.5 py-2 bg-white/70 dark:bg-arena-900/40
                    ring-1 ring-inset ring-white/50 dark:ring-arena-600/30">
      <div className="text-[9.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">{label}</div>
      <div className="text-[13px] font-bold tabular-nums text-arena-900 dark:text-white">{value}</div>
    </div>
  );
}

function ConfirmModal({ confirming, busy, onCancel, onConfirm }: {
  confirming: { kind: 'cat' | 'master'; payload: any }; busy: boolean;
  onCancel: () => void; onConfirm: () => void;
}) {
  const isMaster = confirming.kind === 'master';
  const enabled = confirming.payload.enabled;
  const title = isMaster
    ? (enabled ? 'Re-enable ALL bot categories?' : 'Disable ALL bot categories?')
    : (enabled ? `Re-enable '${confirming.payload.category}' bots?` : `Disable '${confirming.payload.category}' bots?`);
  const body = isMaster
    ? (enabled
        ? 'All three difficulty categories will resume being picked for new matches. In-flight matches are unaffected.'
        : 'NO bots will be picked for new matchmaking queues across any category. Existing in-flight matches with bots already in them will finish normally. Players who would have been matched against a bot will wait until a human joins.')
    : (enabled
        ? `'${confirming.payload.category}' bots will resume being picked for new matches. Effective within ~5s.`
        : `'${confirming.payload.category}' bots will not be picked for new matches. Existing in-flight matches will finish. Effective within ~5s.`);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-start sm:place-items-center p-4 overflow-y-auto" onClick={onCancel}>
      <motion.div onClick={(e) => e.stopPropagation()}
                  initial={{ opacity: 0, scale: 0.95, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                  className="rounded-2xl max-w-md w-full p-5 space-y-4
                             bg-white dark:bg-arena-800
                             ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                             shadow-2xl">
        <div className="flex items-start gap-3">
          <span className={`grid place-items-center w-10 h-10 rounded-xl shrink-0 ring-1 ${
            enabled
              ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30'
              : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30'
          }`}>
            <Icon name={enabled ? 'play_arrow' : 'block'} size={20} filled />
          </span>
          <div>
            <h3 className="text-[15px] font-bold text-arena-900 dark:text-white">{title}</h3>
            <p className="text-[12.5px] text-arena-600 dark:text-neon-200/75 leading-relaxed mt-1">{body}</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onCancel} disabled={busy}
                  className="px-4 py-2 rounded-full text-[12px] font-bold
                             bg-white text-arena-700 dark:text-neon-200/80 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50
                             dark:bg-arena-700/40 dark:text-neon-200/80 dark:ring-arena-600/40 dark:hover:bg-arena-700/60
                             disabled:opacity-50">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={busy}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-[12px] font-bold transition active:scale-95 disabled:opacity-50 ${
                    enabled
                      ? 'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 shadow-[0_2px_10px_rgba(16,185,129,0.4)]'
                      : 'bg-rose-600 hover:bg-rose-500 active:bg-rose-700 shadow-[0_2px_10px_rgba(244,63,94,0.4)]'
                  }`}>
            {busy ? <Icon name="progress_activity" size={13} className="animate-spin" /> : <Icon name={enabled ? 'play_arrow' : 'block'} size={13} />}
            Confirm {enabled ? 'enable' : 'disable'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success' | 'info'; children: React.ReactNode }) {
  const m = tone === 'error'
    ? 'bg-rose-50 dark:bg-rose-500/10 ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200'
    : tone === 'success' ? 'bg-emerald-50 dark:bg-emerald-500/10 ring-emerald-200/60 dark:ring-emerald-400/30 text-emerald-700 dark:text-emerald-200'
    : 'bg-arena-50/70 dark:bg-arena-800/60 ring-arena-200/60 dark:ring-arena-600/40 text-arena-800 dark:text-neon-200/80';
  return <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[13px] ${m}`}>{children}</div>;
}
